import { AlertCircle, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./alert";
import { Button } from "./button";
import { cn } from "@/lib/utils";

interface ErrorStateProps {
  /**
   * Error message to display to the user
   */
  message?: string;

  /**
   * Optional detailed error description
   */
  description?: string;

  /**
   * Callback function to retry the failed operation
   */
  onRetry?: () => void;

  /**
   * Custom retry button text
   * @default "Try Again"
   */
  retryText?: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Display variant - compact for inline errors, full for page-level errors
   * @default "full"
   */
  variant?: "compact" | "full";
}

/**
 * ErrorState Component
 *
 * Displays user-friendly error messages with optional retry functionality.
 * Designed for use throughout the application for consistent error handling UX.
 *
 * @example
 * ```tsx
 * // Page-level error
 * if (error) {
 *   return <ErrorState message="Failed to load assets" onRetry={refetch} />;
 * }
 *
 * // Inline error with custom description
 * <ErrorState
 *   variant="compact"
 *   message="Connection failed"
 *   description="Check your wallet connection"
 *   onRetry={reconnect}
 * />
 * ```
 */
export function ErrorState({
  message = "Something went wrong",
  description,
  onRetry,
  retryText = "Try Again",
  className,
  variant = "full",
}: ErrorStateProps) {
  if (variant === "compact") {
    return (
      <Alert variant="destructive" className={cn("my-4", className)}>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{message}</AlertTitle>
        {description && <AlertDescription>{description}</AlertDescription>}
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="mt-3 border-destructive/50 hover:bg-destructive/10"
          >
            <RefreshCw className="w-3 h-3 mr-2" />
            {retryText}
          </Button>
        )}
      </Alert>
    );
  }

  return (
    <div className={cn("min-h-screen bg-bg-light", className)}>
      <div className="max-w-2xl mx-auto px-8 py-24 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-red-50 border border-red-200 mb-8 shadow-sm">
          <AlertCircle className="w-10 h-10 text-red-500" />
        </div>

        <h2 className="text-3xl font-bold text-text-primary mb-4">{message}</h2>

        {description && (
          <p className="text-text-muted text-lg mb-8">{description}</p>
        )}

        {onRetry && (
          <Button
            variant="outline"
            size="lg"
            onClick={onRetry}
            className="border-red-200 hover:bg-red-50 text-red-600"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {retryText}
          </Button>
        )}
      </div>
    </div>
  );
}
